import React from "react";
import { motion } from "framer-motion";
import BackHeader from "../components/hub/BackHeader";

const TripHelp = ({ onTabChange }) => {

    const sections = [
        {
            title: "Create a Trip",
            icon: "🗺",
            text: "When you generate an itinerary using Expeditio AI, a Trip Hub is automatically created. This hub stores your itinerary, packing list, tickets, documents, expenses and collaboration tools."
        },

        {
            title: "Trip Hub",
            icon: "🏠",
            text: "The Trip Hub is your central control panel where you manage everything related to your trip including itinerary, packing list, bookings and collaborators."
        },

        {
            title: "Copy a Trip",
            icon: "📋",
            text: "If someone shares a trip publicly, you can copy it to your own dashboard. Once copied, it becomes your trip and changes you make will NOT affect the original trip."
        },

        {
            title: "Invite Collaborators",
            icon: "👥",
            text: "You can invite friends or family to plan the trip together. Simply generate an invite link and send it to them."
        },

        {
            title: "Roles Explained",
            icon: "🔑",
            text: "Owner: Full control of the trip. Editor: Can modify itinerary, packing list and details. Viewer: Can only view the trip."
        },

        {
            title: "Share Trip Publicly",
            icon: "🔗",
            text: "You can create a share link to show your itinerary publicly. Others can view the trip and optionally copy it."
        },

        {
            title: "Private Sections",
            icon: "🔒",
            text: "Certain sections like documents, personal notes and private files are only visible to trip members."
        },

        {
            title: "Packing List",
            icon: "🎒",
            text: "Use the packing list to organize items for your trip. You can mark items as packed and track your preparation progress."
        },

        {
            title: "Tickets & Bookings",
            icon: "🎫",
            text: "Store your flight tickets, train tickets or booking details inside the trip so everyone in the group can access them."
        },

        {
            title: "Expenses",
            icon: "💳",
            text: "Track trip expenses like hotels, food or transportation to estimate the total travel budget."
        },

        {
            title: "Documents",
            icon: "📂",
            text: "Upload important travel documents such as passports, visas or reservations for easy access during your trip."
        },

        {
            title: "Trip Collaboration",
            icon: "🤝",
            text: "Multiple travelers can collaborate on the same trip. Editors can modify details while viewers can only view the information."
        }

    ];

    const faqs = [

        {
            q: "If someone copies my trip, will my changes update their copy?",
            a: "No. Once someone copies your trip, it becomes completely independent. Their trip will not change even if you update your original trip."
        },

        {
            q: "Can I remove collaborators?",
            a: "Yes. Trip owners can remove collaborators or change their roles at any time."
        },

        {
            q: "Can viewers edit the trip?",
            a: "No. Viewers can only see the trip details. Only editors and owners can modify the trip."
        },

        {
            q: "Is my trip private?",
            a: "Yes. Trips are private by default. They only become public when you generate a share link."
        },

        {
            q: "Can I copy my own trip?",
            a: "Yes. You can duplicate your own trip to experiment with different plans."
        }

    ];

    return (

        <div className="max-w-4xl mx-auto px-6 py-6 md:py-16">

            {onTabChange && (
                <BackHeader title="Help Center" onBack={() => onTabChange("menu")} />
            )}

            {/* HEADER */}

            <div className="text-center mb-14">

                <h1 className="text-4xl font-bold text-gray-900 mb-4">
                    Trip Help Center
                </h1>

                <p className="text-gray-500 text-lg">
                    Learn how trips, sharing and collaboration work in Expeditio.
                </p>

            </div>

            {/* GUIDE SECTIONS */}

            <div className="space-y-6">

                {sections.map((item, index) => (
                    <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm"
                    >

                        <div className="flex gap-4">

                            <div className="text-2xl">
                                {item.icon}
                            </div>

                            <div>

                                <h3 className="font-bold text-gray-900 text-lg mb-1">
                                    {item.title}
                                </h3>

                                <p className="text-gray-600 text-sm leading-relaxed">
                                    {item.text}
                                </p>

                            </div>

                        </div>

                    </motion.div>
                ))}

            </div>

            {/* FAQ */}

            <div className="mt-16">

                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    Frequently Asked Questions
                </h2>

                <div className="space-y-4">

                    {faqs.map((faq, index) => (
                        <div
                            key={index}
                            className="bg-white border border-gray-100 rounded-xl p-5"
                        >

                            <h4 className="font-semibold text-gray-900 mb-1">
                                {faq.q}
                            </h4>

                            <p className="text-gray-600 text-sm">
                                {faq.a}
                            </p>

                        </div>
                    ))}

                </div>

            </div>

            {/* FOOTER */}

            <div className="mt-16 text-center text-sm text-gray-400">

                Still confused? Reach out via the Help section or contact the Expeditio team.

            </div>

        </div>

    );

};

export default TripHelp;